import React from 'react';
import AppTable, {TableColumn, ActionItem} from '../../../Presentation/components/ui/AppTable';
import {Badge} from '../../../Presentation/components/ui/Badge';
import {Pencil, Trash2, Send, Archive} from 'lucide-react';
import {Checklist, ChecklistStatus, STATUS_LABELS} from '../models/checklist.model';

interface ChecklistsTableProps {
  checklists: Checklist[];
  loading?: boolean;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (row: Checklist) => void;
  onSetStatus: (row: Checklist, status: ChecklistStatus) => void;
  onDelete: (row: Checklist) => void;
}

const STATUS_VARIANT: Record<ChecklistStatus, 'success' | 'warning' | 'secondary'> = {
  published: 'success',
  draft: 'warning',
  archived: 'secondary'
};

const ChecklistsTable: React.FC<ChecklistsTableProps> = ({
  checklists, loading, page, pageSize, totalCount, onPageChange, onPageSizeChange,
  canUpdate, canDelete, onEdit, onSetStatus, onDelete
}) => {
  const columns: TableColumn<Checklist>[] = [
    {key: 'name', title: 'الاسم', render: (r) => r.name},
    {
      key: 'status',
      title: 'الحالة',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABELS[r.status]}</Badge>
    },
    {key: 'items', title: 'عدد البنود', render: (r) => (r.items ? r.items.length : '—')},
    {
      key: 'effective_date',
      title: 'تاريخ السريان',
      render: (r) => (r.effective_date ? r.effective_date.slice(0, 10) : '—')
    }
  ];

  const buildActions = (row: Checklist): ActionItem[] => {
    const actions: ActionItem[] = [];
    if (canUpdate) {
      actions.push({label: 'تعديل', icon: <Pencil size={16} />, onClick: () => onEdit(row)});
      if (row.status !== 'published') {
        actions.push({label: 'نشر', icon: <Send size={16} />, onClick: () => onSetStatus(row, 'published')});
      }
      if (row.status !== 'archived') {
        actions.push({label: 'أرشفة', icon: <Archive size={16} />, onClick: () => onSetStatus(row, 'archived')});
      }
      if (row.status === 'archived') {
        actions.push({label: 'إعادة إلى مسودة', icon: <Pencil size={16} />, onClick: () => onSetStatus(row, 'draft')});
      }
    }
    if (canDelete) {
      actions.push({label: 'حذف', icon: <Trash2 size={16} />, onClick: () => onDelete(row), danger: true});
    }
    return actions;
  };

  return (
    <AppTable<Checklist>
      columns={columns}
      data={checklists}
      rowKey={(row) => row.id}
      loading={loading}
      emptyText="لا توجد قوائم تحقق"
      page={page}
      pageSize={pageSize}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      actions={(canUpdate || canDelete) ? buildActions : undefined}
    />
  );
};

export default ChecklistsTable;
